import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'https://stock-api-production-913a.up.railway.app';

// ★ 台灣慣例：紅漲綠跌
const UP_COLOR = '#d62828';
const DOWN_COLOR = '#00a14b';

const S = {
  sec: { background: '#0a0f1a', border: '1px solid #1a2a3c', borderRadius: 8, padding: '16px 18px', marginBottom: 14 },
  title: { color: '#7ab0cc', fontSize: 13, fontWeight: 700, letterSpacing: 1, marginBottom: 14 },
  num: (v) => ({ color: v > 0 ? UP_COLOR : v < 0 ? DOWN_COLOR : '#aaa', fontWeight: 700 }),
};

const fmt = (v, dec=2) => v == null ? '-' : Number(v).toFixed(dec);
const fmtDate = s => s ? s.slice(5) : '';

// ───── K 線圖（自寫 SVG，含 hover tooltip） ─────
function CandlestickChart({ data, height = 240 }) {
  const [hover, setHover] = React.useState(null);
  const valid = data.filter(d => d.taiex != null);
  if (!valid.length) return null;

  const lows = valid.map(d => d.taiex_low ?? d.taiex);
  const highs = valid.map(d => d.taiex_high ?? d.taiex);
  const minP = Math.min(...lows);
  const maxP = Math.max(...highs);
  const pad = (maxP - minP) * 0.05 || 1;
  const yMin = minP - pad, yMax = maxP + pad;

  const W = 1000, H = height;
  const padL = 55, padR = 12, padT = 10, padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const xStep = innerW / valid.length;
  const candleW = Math.max(2, xStep * 0.6);
  const yScale = p => padT + (yMax - p) / (yMax - yMin) * innerH;
  const xScale = i => padL + (i + 0.5) * xStep;

  const yTicks = [];
  for (let i = 0; i <= 4; i++) yTicks.push(yMin + (yMax - yMin) * i / 4);

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        style={{ width: '100%', height: H, display: 'block' }}
        onMouseLeave={() => setHover(null)}>
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={yScale(v)} y2={yScale(v)}
              stroke="#1a2a3a" strokeDasharray="3 3" />
            <text x={padL - 4} y={yScale(v) + 4} fill="#5a8090" fontSize={10} textAnchor="end">
              {v.toFixed(0)}
            </text>
          </g>
        ))}
        {valid.map((d, i) => i % Math.ceil(valid.length / 8) === 0 && (
          <text key={`x${i}`} x={xScale(i)} y={H - 6} fill="#5a8090" fontSize={10} textAnchor="middle">
            {d.date?.slice(5)}
          </text>
        ))}
        {valid.map((d, i) => {
          const o = d.taiex_open ?? d.taiex;
          const c = d.taiex;
          const h = d.taiex_high ?? Math.max(o, c);
          const l = d.taiex_low ?? Math.min(o, c);
          const isUp = c >= o;
          const color = isUp ? UP_COLOR : DOWN_COLOR;
          const cx = xScale(i);
          const bodyTop = yScale(Math.max(o, c));
          const bodyBottom = yScale(Math.min(o, c));
          return (
            <g key={i} onMouseEnter={() => setHover({ d, i })}>
              <rect x={cx - xStep/2} y={padT} width={xStep} height={innerH}
                fill="transparent" />
              <line x1={cx} x2={cx} y1={yScale(h)} y2={yScale(l)}
                stroke={color} strokeWidth={1} />
              <rect x={cx - candleW/2} y={bodyTop} width={candleW}
                height={Math.max(1, bodyBottom - bodyTop)} fill={color} />
            </g>
          );
        })}
      </svg>
      {hover && (
        <div style={{
          position:'absolute', top: 8, right: 8,
          background:'#0d1820', border:'1px solid #2a4060', padding:'6px 10px',
          fontSize:11, color:'#c0d8ea', pointerEvents:'none', lineHeight:1.5
        }}>
          <div style={{ color:'#90c0dc', fontWeight:700, marginBottom:2 }}>{hover.d.date}</div>
          <div>開 {fmt(hover.d.taiex_open)}　高 {fmt(hover.d.taiex_high)}</div>
          <div>低 {fmt(hover.d.taiex_low)}　收 <span style={{ color: hover.d.taiex >= (hover.d.taiex_open ?? hover.d.taiex) ? UP_COLOR : DOWN_COLOR, fontWeight:700 }}>{fmt(hover.d.taiex)}</span></div>
        </div>
      )}
    </div>
  );
}

export default function MarketOverview() {
  // 手機偵測
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [daily, setDaily] = useState([]);
  const [sector, setSector] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState('');
  const [events, setEvents] = useState({});
  const [m1b, setM1b] = useState([]);
  const [cmpDaily, setCmpDaily] = useState([]);
  const [eventsTab, setEventsTab] = useState('announcement');
  const [annCat, setAnnCat] = useState('全部');
  
  // 市場訊號 state
  const [signals, setSignals] = useState(null);
  const [signalsHistory, setSignalsHistory] = useState([]);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/market/daily?days=20`).then(r => r.json()),
      fetch(`${API_URL}/api/market/sector`).then(r => r.json()),
    ]).then(([d, s]) => {
      setDaily(Array.isArray(d) ? d : []);
      setSector(Array.isArray(s) ? s : []);
      if (d.length) setLastUpdate(d[d.length-1]?.date || '');
      setLoading(false);
    }).catch(() => setLoading(false));

    // 抓事件資料（各類型分開呼叫，避免混在一起時被同一個LIMIT互相排擠）
    const types = ['attention','disposal','treasury','insider','announcement','lawsuit'];
    Promise.all(
      types.map(t =>
        fetch(`${API_URL}/api/market/events?type=${t}&days=7&limit=300`)
          .then(r => r.json())
          .then(d => ({ type: t, events: d.ok ? d.events : [] }))
          .catch(() => ({ type: t, events: [] }))
      )
    ).then(results => {
      const grouped = {};
      results.forEach(({ type: t, events: evs }) => { grouped[t] = evs; });
      setEvents(grouped);
    });

    // 抓 M1B
    fetch(`${API_URL}/api/market/m1b?months=36`)
      .then(r => r.json()).then(d => {
        if (d.ok) setM1b(d.data);
      }).catch(() => {});

    // 抓比較圖長序列(120天,給20日變化率曲線用)
    fetch(`${API_URL}/api/market/daily?days=120`)
      .then(r => r.json()).then(d => {
        if (Array.isArray(d)) setCmpDaily(d);
      }).catch(() => {});

    // 抓市場訊號資料
    fetch(`${API_URL}/api/market-signals/latest`)
      .then(r => r.json()).then(d => {
        if (!d.error) setSignals(d);
      }).catch(() => {});
    fetch(`${API_URL}/api/market-signals/history?days=180`)
      .then(r => r.json()).then(d => {
        if (d.data) setSignalsHistory(d.data);
      }).catch(() => {});
  }, []);

  if (loading) return <div style={{ padding: 24, color: '#4a6878' }}>⏳ 載入中...</div>;

  if (!daily.length) return (
    <div style={{ padding: 24, color: '#4a6878', textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
      <div>尚無大盤資料，請等待今日自動更新後再查看</div>
      <div style={{ fontSize: 12, marginTop: 8 }}>每個交易日下午 2:30 自動更新</div>
    </div>
  );

  const latest = daily[daily.length - 1] || {};
  const hasTaiex = daily.some(d => d.taiex);
  const hasInstitutional = daily.some(d => d.foreign_net != null);
  const hasMargin = daily.some(d => d.margin_bal != null);

  // 手機版 S.sec padding 縮小
  const _sec = isMobile ? { ...S.sec, padding: '10px 10px' } : S.sec;

  return (
    <div style={{ padding: isMobile ? '4px' : '8px 12px', maxWidth: 1100 }}>
      {/* 標題列 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#90c0dc', margin: 0 }}>📊 大盤資訊</h2>
        <span style={{ color: '#3a5060', fontSize: 12 }}>近20個交易日滾動更新</span>
        {lastUpdate && <span style={{ marginLeft: 'auto', color: '#3a5060', fontSize: 11 }}>最新日期：{lastUpdate}</span>}
      </div>

      {/* ── 1-4. 四張主要圖表（2x2 排列）── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 10 : 14, marginBottom: 14 }}>
      {/* ── 1. 加權指數 ── */}
      {hasTaiex && (
        <div style={{..._sec, marginBottom: 0}}>
          <div style={S.title}>📈 加權指數走勢</div>
          <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
            {latest.taiex && <div>
              <div style={{ color: '#5a8090', fontSize: 11 }}>收盤指數</div>
              <div style={{ color: '#c0d8ea', fontSize: 22, fontWeight: 700 }}>{fmt(latest.taiex, 2)}</div>
            </div>}
            {latest.taiex_chg != null && <div>
              <div style={{ color: '#5a8090', fontSize: 11 }}>漲跌</div>
              <div style={{ fontSize: 18, fontWeight: 700, ...S.num(latest.taiex_chg) }}>
                {latest.taiex_chg > 0 ? '+' : ''}{fmt(latest.taiex_chg, 2)}
              </div>
            </div>}
            {latest.taiex_chg_pct != null && <div>
              <div style={{ color: '#5a8090', fontSize: 11 }}>漲跌幅</div>
              <div style={{ fontSize: 18, fontWeight: 700, ...S.num(latest.taiex_chg_pct) }}>
                {latest.taiex_chg_pct > 0 ? '+' : ''}{fmt(latest.taiex_chg_pct, 2)}%
              </div>
            </div>}
          </div>
          <CandlestickChart data={daily} height={240} />
          <div style={{ marginTop: 16, color: '#7ab0cc', fontSize: 12, fontWeight: 700 }}>
            💰 成交值（億元）
          </div>
          <ResponsiveContainer width="100%" height={100}>
            <BarChart data={daily} margin={{ top: 4, right: 12, left: 12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2a3a" />
              <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fill: '#5a8090', fontSize: 10 }} />
              <YAxis tick={{ fill: '#5a8090', fontSize: 10 }} />
              <Tooltip formatter={(v) => [fmt(v) + ' 億', '成交值']} labelFormatter={l => `日期：${l}`}
                contentStyle={{ background:'#0d1820', border:'1px solid #2a4060', fontSize:11 }} />
              <Bar dataKey="value" name="成交值(億)" fill="#4C9BB8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── 2. 漲跌家數最近 20 天堆疊圖 ── */}
      <div style={{..._sec, marginBottom: 0}}>
        <div style={S.title}>📊 漲跌家數（最近 20 天）</div>
        <div style={{ display: 'flex', gap: 20, marginBottom: 10, fontSize: 13 }}>
          <div><span style={{ color: '#5a8090' }}>上漲 </span>
            <span style={{ color: UP_COLOR, fontWeight: 700 }}>{latest.up ?? '-'}</span> 檔
          </div>
          <div><span style={{ color: '#5a8090' }}>平盤 </span>
            <span style={{ color: '#aaa', fontWeight: 700 }}>{latest.flat ?? '-'}</span> 檔
          </div>
          <div><span style={{ color: '#5a8090' }}>下跌 </span>
            <span style={{ color: DOWN_COLOR, fontWeight: 700 }}>{latest.down ?? '-'}</span> 檔
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={daily} margin={{ top: 4, right: 12, left: 12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1a2a3a" />
            <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fill: '#5a8090', fontSize: 10 }} />
            <YAxis tick={{ fill: '#5a8090', fontSize: 10 }} />
            <Tooltip contentStyle={{ background:'#0d1820', border:'1px solid #2a4060', fontSize:11 }} />
            <Bar dataKey="up" name="上漲" fill={UP_COLOR} stackId="a" />
            <Bar dataKey="flat" name="平盤" fill="#555" stackId="a" />
            <Bar dataKey="down" name="下跌" fill={DOWN_COLOR} stackId="a" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ── 4. 三大法人最近 20 天買賣超表格 ── */}
      {hasInstitutional && (
        <div style={{..._sec, marginBottom: 0}}>
          <div style={S.title}>🏛️ 三大法人買賣超（最近 20 天，單位：億）</div>
          <div style={{ maxHeight: 460, overflowY: 'auto', border: '1px solid #1a2a3a', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'monospace' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#0d1820', zIndex: 1 }}>
                <tr style={{ borderBottom: '1px solid #2a3a4a' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', color: '#7ab0cc', fontWeight: 600 }}>日期</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', color: '#7ab0cc', fontWeight: 600 }}>外資</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', color: '#7ab0cc', fontWeight: 600 }}>投信</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', color: '#7ab0cc', fontWeight: 600 }}>自營商</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', color: '#7ab0cc', fontWeight: 600, borderLeft: '1px solid #2a3a4a' }}>合計</th>
                </tr>
              </thead>
              <tbody>
                {[...daily].reverse().map((d, i) => {
                  const f = d.foreign_net, t = d.trust_net, dl = d.dealer_net;
                  const tot = [f, t, dl].filter(v => typeof v === 'number').reduce((a, b) => a + b, 0);
                  const cellStyle = (v) => ({
                    padding: '6px 10px', textAlign: 'right',
                    color: v == null ? '#555' : (v > 0 ? UP_COLOR : (v < 0 ? DOWN_COLOR : '#888')),
                  });
                  return (
                    <tr key={d.date || i} style={{ borderBottom: '1px solid #131c26' }}>
                      <td style={{ padding: '6px 10px', color: '#c0d8ea' }}>{d.date}</td>
                      <td style={cellStyle(f)}>{f != null ? (f > 0 ? '+' : '') + fmt(f) : '-'}</td>
                      <td style={cellStyle(t)}>{t != null ? (t > 0 ? '+' : '') + fmt(t) : '-'}</td>
                      <td style={cellStyle(dl)}>{dl != null ? (dl > 0 ? '+' : '') + fmt(dl) : '-'}</td>
                      <td style={{ ...cellStyle(tot), borderLeft: '1px solid #1a2a3a', fontWeight: 700 }}>
                        {(tot > 0 ? '+' : '') + fmt(tot)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── 5. 融資融券最近 20 天表格 ── */}
      {hasMargin && (
        <div style={{..._sec, marginBottom: 0}}>
          <div style={S.title}>💰 融資融券餘額（最近 20 天）</div>
          <div style={{ maxHeight: 460, overflowY: 'auto', border: '1px solid #1a2a3a', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: 'monospace' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#0d1820', zIndex: 1 }}>
                <tr style={{ borderBottom: '1px solid #2a3a4a' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', color: '#7ab0cc', fontWeight: 600 }}>日期</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', color: '#7ab0cc', fontWeight: 600 }}>融資餘額（億）</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', color: '#7ab0cc', fontWeight: 600 }}>日增減</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', color: '#7ab0cc', fontWeight: 600, borderLeft: '1px solid #2a3a4a' }}>融券餘額（千張）</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', color: '#7ab0cc', fontWeight: 600 }}>日增減</th>
                </tr>
              </thead>
              <tbody>
                {[...daily].reverse().map((d, i, arr) => {
                  // 跟前一天比（陣列已 reverse，所以「前一天」是 arr[i+1]）
                  const prev = arr[i + 1] || {};
                  const mDiff = (d.margin_bal != null && prev.margin_bal != null) ? d.margin_bal - prev.margin_bal : null;
                  const sDiff = (d.short_bal != null && prev.short_bal != null) ? d.short_bal - prev.short_bal : null;
                  const diffStyle = (v) => ({
                    padding: '6px 10px', textAlign: 'right', fontSize: 12,
                    color: v == null ? '#555' : (v > 0 ? UP_COLOR : (v < 0 ? DOWN_COLOR : '#888')),
                  });
                  return (
                    <tr key={d.date || i} style={{ borderBottom: '1px solid #131c26' }}>
                      <td style={{ padding: '6px 10px', color: '#c0d8ea' }}>{d.date}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#4C9BB8', fontWeight: 600 }}>
                        {d.margin_bal != null ? fmt(d.margin_bal) : '-'}
                      </td>
                      <td style={diffStyle(mDiff)}>
                        {mDiff != null ? (mDiff > 0 ? '+' : '') + fmt(mDiff) : '-'}
                      </td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', color: '#f5a623', fontWeight: 600, borderLeft: '1px solid #1a2a3a' }}>
                        {d.short_bal != null ? fmt(d.short_bal) : '-'}
                      </td>
                      <td style={diffStyle(sDiff)}>
                        {sDiff != null ? (sDiff > 0 ? '+' : '') + fmt(sDiff) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>

      {/* ── 6. 類股漲跌排行（左漲右跌兩欄，各取前 20 名）── */}
      {sector.length > 0 && (() => {
        const upList = sector.filter(s => s.avg_chg >= 0).sort((a, b) => b.avg_chg - a.avg_chg).slice(0, 20);
        const downList = sector.filter(s => s.avg_chg < 0).sort((a, b) => a.avg_chg - b.avg_chg).slice(0, 20);
        const maxRows = Math.max(upList.length, downList.length);
        const colHeight = Math.max(200, maxRows * 22);
        const SectorCol = ({ data, color, title, totalCount }) => (
          <div>
            <div style={{ color: color, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
              {title}（前 {data.length}{totalCount > data.length ? ` / 共 ${totalCount}` : ''}）
            </div>
            <ResponsiveContainer width="100%" height={colHeight}>
              {/* [MARKET_YAXIS_PATCHED] interval=0 強制顯示所有 tick, top margin 加大避免裁切 */}
              <BarChart data={data} layout="vertical" margin={{ top: 10, right: 50, left: 90, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a2a3a" />
                <XAxis type="number" tickFormatter={v => `${v}%`} tick={{ fill: '#5a8090', fontSize: 10 }} />
                <YAxis type="category" dataKey="industry" width={85} interval={0} tick={{ fill: '#90c0dc', fontSize: 10 }} />
                <Tooltip formatter={(v) => [`${v}%`, '平均漲跌幅']}
                  contentStyle={{ background:'#0d1820', border:'1px solid #2a4060', fontSize:12 }} />
                <Bar dataKey="avg_chg" name="漲跌幅" fill={color} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
        return (
          <div style={_sec}>
            <div style={S.title}>🏭 類股漲跌排行（最新一日平均漲跌幅）</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 10 : 16 }}>
              <SectorCol data={upList} color={UP_COLOR} title="📈 上漲類股"
                totalCount={sector.filter(s => s.avg_chg >= 0).length} />
              <SectorCol data={downList} color={DOWN_COLOR} title="📉 下跌類股"
                totalCount={sector.filter(s => s.avg_chg < 0).length} />
            </div>
          </div>
        );
      })()}

      {!hasInstitutional && !hasMargin && (
        <div style={{ color: '#4a6878', fontSize: 12, textAlign: 'center', padding: 16 }}>
          三大法人及融資融券資料將於首次自動更新後顯示
        </div>
      )}

      {/* ───── 市場訊號監控 ───── */}
      {signals && (() => {
        const BUCKET_COLOR = {
          '強多': '#a91d1d', '多': '#d62828', '弱多': '#e85a5a',
          '中性': '#5a8090',
          '弱空': '#6cb88a', '空': '#00a14b', '強空': '#007a3a',
        };
        const SIG_COLOR_GOOD = '#d62828';
        const SIG_COLOR_BAD = '#00a14b';
        const scoreColor = (s) => s == null ? '#5a8090' : s > 0 ? SIG_COLOR_GOOD : s < 0 ? SIG_COLOR_BAD : '#5a8090';
        const sigNum = (v) => ({ color: v == null ? '#5a8090' : v > 0 ? SIG_COLOR_GOOD : v < 0 ? SIG_COLOR_BAD : '#5a8090', fontWeight: 700 });
        
        // 各訊號的當前狀況描述
        const desc = {
          s1: signals.s1_pct >= 80 ? '散戶槓桿過熱（風險高）' :
              signals.s1_pct >= 60 ? '槓桿偏熱' :
              signals.s1_pct >= 40 ? '中性' :
              signals.s1_pct >= 20 ? '槓桿偏冷' : '槓桿極低（潛在反彈）',
          s2: signals.s2_pct >= 80 ? '外資多單偏多' :
              signals.s2_pct >= 60 ? '外資略偏多' :
              signals.s2_pct >= 40 ? '中性' :
              signals.s2_pct >= 20 ? '外資略偏空' : '外資空單偏多',
          s3: signals.s3_pct >= 80 ? '看空力道強增（風險高）' :
              signals.s3_pct >= 60 ? '看空力道增加' :
              signals.s3_pct >= 40 ? '中性' :
              signals.s3_pct >= 20 ? '看空力道減弱' : '看空動能大降',
          s4: signals.s4_raw >= 1500 ? '法人強力買超' :
              signals.s4_raw >= 500 ? '法人買超' :
              signals.s4_raw >= -500 ? '中性' :
              signals.s4_raw >= -1500 ? '法人賣超' : '法人強力賣超',
          s5: signals.s5_pct >= 80 ? 'ETF 槓桿過熱（風險高）' :
              signals.s5_pct >= 60 ? 'ETF 槓桿偏熱' :
              signals.s5_pct >= 40 ? '中性' :
              signals.s5_pct >= 20 ? 'ETF 槓桿偏冷' : 'ETF 槓桿極低',
          s6: signals.s6_quadrant === 1 ? '全民熱（反指標空）' :
              signals.s6_quadrant === 2 ? '進攻主導（反指標空）' :
              signals.s6_quadrant === 3 ? '散戶情緒低落（反指標多）' :
              signals.s6_quadrant === 4 ? '避險主導（反指標多）' : '—',
        };
        const bucketBg = BUCKET_COLOR[signals.bucket] || '#5a8090';
        const compositeStr = signals.composite >= 0 ? `+${signals.composite.toFixed(2)}` : signals.composite.toFixed(2);
        
        // [SIG_DAILY_CHG_V1] chgKey: 對應 prev_ 欄位的 raw 值；bullish: 數值上升是否視為偏多（決定上色方向）
        const SIG = [
          { k: 's1', role: '風險', name: '融資使用率（信用代理）', valLabel: `百分位 ${fmt(signals.s1_pct, 1)}`, chgKey: 's1_pct', curVal: signals.s1_pct, bullish: false, prevLabel: (p) => `百分位 ${fmt(p,1)}` },
          { k: 's2', role: '方向', name: '外資台指期未平倉', valLabel: `百分位 ${fmt(signals.s2_pct, 1)}`, chgKey: 's2_pct', curVal: signals.s2_pct, bullish: true, prevLabel: (p) => `百分位 ${fmt(p,1)}` },
          { k: 's3', role: '風險', name: '借券 5 日變化', valLabel: `百分位 ${fmt(signals.s3_pct, 1)}`, chgKey: 's3_pct', curVal: signals.s3_pct, bullish: false, prevLabel: (p) => `百分位 ${fmt(p,1)}` },
          { k: 's4', role: '方向', name: '三大法人 5 日合計', valLabel: `${fmt(signals.s4_raw, 0)} 億`, chgKey: 's4_raw', curVal: signals.s4_raw, bullish: true, prevLabel: (p) => `${fmt(p,0)} 億` },
          { k: 's5', role: '情緒', name: 'ETF 融資滲透率', valLabel: `百分位 ${fmt(signals.s5_pct, 1)}`, chgKey: 's5_pct', curVal: signals.s5_pct, bullish: false, prevLabel: (p) => `百分位 ${fmt(p,1)}` },
          { k: 's6', role: '情緒', name: 'ETF 情緒分流圖', valLabel: `象限 ${signals.s6_quadrant}`, chgKey: null, curVal: null, bullish: null, prevLabel: null },
        ];
        // 計算日變化的小工具：依 bullish 決定紅綠方向（[SIG_PREV_VALUE_V1] 多回傳 prevVal）
        const dailyChg = (s) => {
          if (!s.chgKey || s.curVal == null) return null;
          const prevVal = signals[`prev_${s.chgKey}`];
          if (prevVal == null) return null;
          const diff = s.curVal - prevVal;
          const isUp = diff > 0;
          const color = diff === 0 ? '#5a8090' : (isUp === s.bullish ? SIG_COLOR_GOOD : SIG_COLOR_BAD);
          return { diff, color, prevVal };
        };
        
        return (
          <div style={_sec}>
            <div style={S.title}>📊 市場訊號監控（6 訊號 + 環境變數）</div>
            <div style={{ fontSize: 12, color: '#5a8090', marginBottom: 14 }}>
              {signals.date} ｜ TAIEX {signals.taiex?.toLocaleString()} ｜ 更新 {signals.updated_at?.slice(0, 19)}
            </div>
            
            {/* Composite Score */}
            <div style={{
              background: '#060c14',
              borderLeft: `4px solid ${bucketBg}`,
              padding: '14px 16px', borderRadius: 6, marginBottom: 16,
              display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
            }}>
              <div>
                <div style={{ fontSize: 11, color: '#5a8090' }}>Composite Score</div>
                <div style={{ fontSize: isMobile ? 26 : 32, fontWeight: 700, color: bucketBg }}>
                  {compositeStr}
                  <span style={{
                    background: bucketBg, color: 'white',
                    padding: '3px 10px', borderRadius: 4, fontSize: 14, marginLeft: 10,
                    verticalAlign: 'middle',
                  }}>{signals.bucket}</span>
                </div>
                {/* [COMPOSITE_TREND_BIG_V1] 今昨轉折大字 */}
                {signals.prev_composite != null && (() => {
                  const d = signals.composite - signals.prev_composite;
                  const flat = Math.abs(d) < 0.005, up = d > 0;
                  return (
                    <div style={{ fontSize: isMobile ? 18 : 24, fontWeight: 800, marginTop: 6,
                      color: flat ? '#5a8090' : (up ? SIG_COLOR_GOOD : SIG_COLOR_BAD) }}>
                      {flat ? '― 持平' : (up ? '▲ 轉好' : '▼ 轉差')}
                      {flat ? '' : ` ${d >= 0 ? '+' : ''}${d.toFixed(2)}`}
                    </div>
                  );
                })()}
                {/* [COMPOSITE_PREV_V1] 昨日數值對照，淡色小字 */}
                {signals.prev_composite != null && (
                  <div style={{ fontSize: 12, color: '#5a8090', marginTop: 4 }}>
                    昨日 {signals.prev_composite >= 0 ? '+' : ''}{signals.prev_composite.toFixed(2)}
                    {signals.prev_bucket ? `（${signals.prev_bucket}）` : ''}
                  </div>
                )}
              </div>
            </div>
            
            {/* 6 訊號狀態 */}
            <div style={{ fontSize: 13, color: '#7ab0cc', fontWeight: 600, marginBottom: 8 }}>6 訊號當前狀態</div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
              gap: 8, marginBottom: 16,
            }}>
              {SIG.map(s => {
                const score = signals[`${s.k}_score`];
                return (
                  <div key={s.k} style={{
                    background: '#060c14', border: '1px solid #1a2a3c',
                    borderRadius: 6, padding: '10px 12px',
                  }}>
                    <div style={{ fontSize: 10, color: '#5a8090' }}>{s.role} ｜ {s.name}</div>
                    {signals.source_dates?.[s.k] && (
                      <div style={{ fontSize: 9, color: '#4a6878', marginBottom: 2 }}>資料日 {signals.source_dates[s.k].slice(5)}</div>
                    )}
                    <div style={{ fontSize: 13, color: '#d8e4ee', margin: '3px 0', fontWeight: 600 }}>
                      {s.valLabel}
                      <span style={{ color: scoreColor(score), fontSize: 14, marginLeft: 6 }}>●</span>
                    </div>
                    {/* [SIG_DAILY_CHG_V1] 今日變化（百分點差異，依多空判斷顏色） */}
                    {(() => {
                      const chg = dailyChg(s);
                      if (!chg) return null;
                      return (
                        <React.Fragment>
                          {/* [SIG_TREND_BIG_V1] 轉折大字：紅=轉好 綠=轉差 */}
                          <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 800, color: chg.color, marginBottom: 1 }}>
                            {chg.color === SIG_COLOR_GOOD ? '▲ 轉好' : chg.color === SIG_COLOR_BAD ? '▼ 轉差' : '― 持平'}
                          </div>
                          <div style={{ fontSize: 11, color: chg.color, fontWeight: 600, marginBottom: 2 }}>
                            較昨日 {chg.diff >= 0 ? '+' : ''}{chg.diff.toFixed(1)}
                          </div>
                          {/* [SIG_PREV_VALUE_V1] 昨日完整數值，淡色 */}
                          {s.prevLabel && (
                            <div style={{ fontSize: 10, color: '#5a8090', marginBottom: 2 }}>
                              昨日 {s.prevLabel(chg.prevVal)}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })()}
                    <div style={{ fontSize: 12, color: scoreColor(score), fontWeight: 600, margin: '4px 0' }}>
                      {desc[s.k]}
                    </div>
                    {s.k === 's5' && signals.etf_margin_chg_20 != null && (() => {
                      const chg20 = signals.etf_margin_chg_20;
                      const streak = signals.etf_margin_streak_10;
                      const chgDesc = chg20 >= 30 ? '高位，散戶積極加碼' :
                                      chg20 >= 10 ? '溫和增加' :
                                      chg20 >= -10 ? '中性' :
                                      chg20 >= -30 ? '溫和減少' : '低位，散戶離場';
                      const streakDesc = streak >= 8 ? '強烈追價' :
                                         streak >= 6 ? '偏買進' :
                                         streak >= 4 ? '中性' :
                                         streak >= 2 ? '偏觀望' : '冷清';
                      return (
                        <div style={{ fontSize: 10, color: '#7a9ab8', borderTop: '1px solid #1a2a3c', paddingTop: 5, marginTop: 5, lineHeight: 1.6 }}>
                          <div>20 日變化：<span style={{ color: '#d8e4ee', fontWeight: 600 }}>{chg20 >= 0 ? '+' : ''}{chg20.toFixed(1)}%</span> ({chgDesc})</div>
                          <div>10 日 streak：<span style={{ color: '#d8e4ee', fontWeight: 600 }}>{streak?.toFixed(0)}/10</span> ({streakDesc})</div>
                        </div>
                      );
                    })()}
                    <div style={{ fontSize: 10, color: '#7a9ab8' }}>
                      score <span style={sigNum(score)}>{fmt(score)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* 環境變數 */}
            <div style={{ fontSize: 13, color: '#7ab0cc', fontWeight: 600, marginBottom: 8 }}>環境變數（外部風險指標）</div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',  /* US10Y_V1: 4卡改2x2排列 */
              gap: 10, marginBottom: 12,
            }}>
              {[
                { title: 'USDTWD 美元兌新台幣', val: signals.env_usdtwd, unit: '', chg1w: signals.env_usdtwd_1w_chg, chg1m: signals.env_usdtwd_1m_chg, chg3m: signals.env_usdtwd_3m_chg, pct1m: signals.env_usdtwd_1m_pct, chgUnit: '%', prevVal: signals.prev_env_usdtwd, srcKey: 'env_usdtwd' },
                { title: 'HY Spread 美國高收益債利差', val: signals.env_hy, unit: '%', chg1w: signals.env_hy_1w_chg, chg1m: signals.env_hy_1m_chg, chg3m: signals.env_hy_3m_chg, pct1m: signals.env_hy_1m_pct, chgUnit: 'bps', prevVal: signals.prev_env_hy, srcKey: 'env_hy' },
                { title: 'US10Y 美國10年期公債殖利率', val: signals.env_us10y, unit: '%', chg1w: signals.env_us10y_1w_chg, chg1m: signals.env_us10y_1m_chg, chg3m: signals.env_us10y_3m_chg, pct1m: signals.env_us10y_1m_pct, chgUnit: 'bps', prevVal: signals.prev_env_us10y, srcKey: 'env_us10y' },  /* US10Y_V1 */
                { title: 'Gold 黃金（美元/盎司）', val: signals.env_gold, unit: '', chg1w: signals.env_gold_1w_chg, chg1m: signals.env_gold_1m_chg, chg3m: signals.env_gold_3m_chg, pct1m: signals.env_gold_1m_pct, chgUnit: '%', prevVal: signals.prev_env_gold, srcKey: 'env_gold' },  /* GOLD_CARD_V1 */
              ].map((e, i) => {
                // [ENV_DAILY_CHG_V1] 今日變化：環境變數皆為「數值漲跌」直接對應紅綠（非多空判斷）
                const todayChg = (e.val != null && e.prevVal != null) ? e.val - e.prevVal : null;
                return (
                <div key={i} style={{
                  background: '#060c14', border: '1px solid #1a2a3c',
                  borderRadius: 6, padding: '12px 14px',
                }}>
                  <div style={{ fontSize: 11, color: '#5a8090', marginBottom: 4 }}>{e.title}
                    {signals.source_dates?.[e.srcKey] && <span style={{ fontSize: 9, color: '#4a6878', marginLeft: 6 }}>資料日 {signals.source_dates[e.srcKey].slice(5)}</span>}
                  </div>
                  <div style={{ fontSize: 20, color: '#d8e4ee', fontWeight: 600, marginBottom: 8 }}>
                    {fmt(e.val, e.chgUnit === 'bps' ? 2 : 3)}{e.unit}
                  </div>
                  <div style={{ display: 'flex', gap: 14, fontSize: 11, flexWrap: 'wrap' }}>
                    {/* [ENV_DAILY_CHG_V1] 今日變化 */}
                    {todayChg != null && (
                      <div>
                        <div style={{ color: '#5a8090' }}>今日</div>
                        <div style={S.num(todayChg)}>{todayChg >= 0 ? '+' : ''}{fmt(todayChg, e.chgUnit === 'bps' ? 0 : 2)} {e.chgUnit}</div>
                      </div>
                    )}
                    <div>
                      <div style={{ color: '#5a8090' }}>1 週</div>
                      <div style={S.num(e.chg1w)}>{e.chg1w >= 0 ? '+' : ''}{fmt(e.chg1w, e.chgUnit === 'bps' ? 0 : 2)} {e.chgUnit}</div>
                    </div>
                    <div>
                      <div style={{ color: '#5a8090' }}>1 月</div>
                      <div style={S.num(e.chg1m)}>{e.chg1m >= 0 ? '+' : ''}{fmt(e.chg1m, e.chgUnit === 'bps' ? 0 : 2)} {e.chgUnit}</div>
                    </div>
                    <div>
                      <div style={{ color: '#5a8090' }}>3 月</div>
                      <div style={S.num(e.chg3m)}>{e.chg3m >= 0 ? '+' : ''}{fmt(e.chg3m, e.chgUnit === 'bps' ? 0 : 2)} {e.chgUnit}</div>
                    </div>
                    {e.pct1m != null && (
                      <div style={{ marginLeft: 'auto' }}>
                        <div style={{ color: '#5a8090' }}>1 月百分位</div>
                        <div style={{ color: '#7ab0cc', fontWeight: 600 }}>{fmt(e.pct1m, 0)} / 100</div>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
            
            {signals.env_label && (
              <div style={{
                background: '#1a1a0a', border: '1px solid #4a3a18',
                padding: '8px 14px', borderRadius: 6, marginBottom: 16,
                color: '#f5c518', fontSize: 13,
              }}>
                <strong>環境標籤：</strong>{signals.env_label}
              </div>
            )}
            
            <div style={{ fontSize: 10, color: '#4a6878', marginTop: 4, lineHeight: 1.6 }}>
              ※ 本區塊呈現當前市場氣氛與情緒狀態，僅供參考，不作為買賣依據。
            </div>
          </div>
        );
      })()}

      {/* ── 融資 vs 大盤 20 日變化率（加權指數走勢 + 雙線 + 背離直條）── */}
      {(() => {
        const base = cmpDaily.filter(d => d.taiex != null && d.margin_bal != null);
        if (base.length < 21) return null;
        const series = [];
        for (let i = 20; i < base.length; i++) {
          const tPct = (base[i].taiex - base[i - 20].taiex) / base[i - 20].taiex * 100;
          const mPct = (base[i].margin_bal - base[i - 20].margin_bal) / base[i - 20].margin_bal * 100;
          series.push({
            date: base[i].date.slice(5),
            taiex: base[i].taiex,
            taiexPct: +tPct.toFixed(2),
            marginPct: +mPct.toFixed(2),
            diff: +(mPct - tPct).toFixed(2),
          });
        }
        // SHOW60_V1: 圖表顯示最近 60 個資料點(20日變化率計算窗口不變)
        const series60 = series.slice(-60);
        const last = series60[series60.length - 1];
        return (
          <div style={_sec}>
            <div style={S.title}>💰 融資 vs 大盤 20 日變化率</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 14 : 28, marginBottom: 12, fontSize: 13 }}>
              <div style={{ color: '#5a8090' }}>最新加權指數 <span style={{ color: '#e0e0e0', fontWeight: 700, fontSize: 18 }}>{Number(last.taiex).toLocaleString()}</span></div>
              <div style={{ color: '#5a8090' }}>最新大盤 <span style={{ ...S.num(last.taiexPct), fontSize: 18 }}>{(last.taiexPct > 0 ? '+' : '') + last.taiexPct}%</span></div>
              <div style={{ color: '#5a8090' }}>最新融資 <span style={{ ...S.num(last.marginPct), fontSize: 18 }}>{(last.marginPct > 0 ? '+' : '') + last.marginPct}%</span></div>
              <div style={{ color: '#5a8090' }}>最新背離差 <span style={{ ...S.num(last.diff), fontSize: 18 }}>{(last.diff > 0 ? '+' : '') + last.diff}%</span></div>
            </div>
            <div style={{ fontSize: 11, color: '#5a8090', margin: '4px 0 4px' }}>加權指數走勢</div>
            <ResponsiveContainer width="100%" height={150}>
              <ComposedChart data={series60} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a2a3a" />
                <XAxis dataKey="date" tick={{ fill: '#5a8090', fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis domain={['auto', 'auto']} tick={{ fill: '#7ab0cc', fontSize: 9 }} tickFormatter={v => (v / 1000).toFixed(0) + 'k'} width={42} />
                <Tooltip formatter={v => [Number(v).toLocaleString(), '加權指數']} labelFormatter={l => `日期 ${l}`} contentStyle={{ background: '#0d1820', border: '1px solid #2a4060', fontSize: 11 }} />
                <Line type="monotone" dataKey="taiex" name="加權指數" stroke="#e0e0e0" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', gap: 18, fontSize: 12, margin: '12px 0 4px' }}>
              <span style={{ color: '#f5c518' }}>■ 大盤20日漲跌幅</span>
              <span style={{ color: '#4C9BB8' }}>■ 融資20日增減率</span>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <ComposedChart data={series60} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a2a3a" />
                <XAxis dataKey="date" tick={{ fill: '#5a8090', fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fill: '#7ab0cc', fontSize: 9 }} tickFormatter={v => `${v}%`} width={40} />
                <ReferenceLine y={0} stroke="#3a5060" />
                <Tooltip formatter={(v, n) => [`${v}%`, n]} labelFormatter={l => `日期 ${l}`} contentStyle={{ background: '#0d1820', border: '1px solid #2a4060', fontSize: 11 }} />
                <Line type="monotone" dataKey="taiexPct" name="大盤20日漲跌幅" stroke="#f5c518" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="marginPct" name="融資20日增減率" stroke="#4C9BB8" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: '#5a8090', margin: '12px 0 4px' }}>背離差（融資-大盤；紅=融資較熱，綠=融資較冷）</div>
            <ResponsiveContainer width="100%" height={120}>
              <ComposedChart data={series60} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a2a3a" />
                <XAxis dataKey="date" tick={{ fill: '#5a8090', fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fill: '#7ab0cc', fontSize: 9 }} tickFormatter={v => `${v}%`} width={40} />
                <ReferenceLine y={0} stroke="#3a5060" />
                <Tooltip formatter={v => [`${v}%`, '背離差']} labelFormatter={l => `日期 ${l}`} contentStyle={{ background: '#0d1820', border: '1px solid #2a4060', fontSize: 11 }} />
                <Bar dataKey="diff" name="背離差">
                  {series.map((e, i) => <Cell key={i} fill={e.diff >= 0 ? '#d62828' : '#00a14b'} />)}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        );
      })()}

      {/* ── 貨幣 M1B 年增率（最近 3 年）── */}
      {m1b.length > 0 && (
        <div style={_sec}>
          <div style={S.title}>💵 貨幣總計數 M1B 年增率（最近 3 年）</div>
          <ResponsiveContainer width="100%" height={180}>
            <ComposedChart data={m1b} margin={{ top: 4, right: 40, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2a3a" />
              <XAxis dataKey="ym" tickFormatter={s => s?.slice(2)} tick={{ fill: '#5a8090', fontSize: 10 }}
                interval={5} />
              <YAxis yAxisId="left" orientation="left"
                tick={{ fill: '#7ab0cc', fontSize: 9 }}
                tickFormatter={v => `${(v/100000).toFixed(0)}千億`}
                width={45} />
              <YAxis yAxisId="right" orientation="right"
                tick={{ fill: '#f5c518', fontSize: 9 }}
                tickFormatter={v => `${v}%`}
                width={36} />
              <ReferenceLine yAxisId="right" y={0} stroke="#3a5060" />
              <Tooltip
                formatter={(v, name) => name === 'M1B餘額'
                  ? [`${(v/100000).toFixed(0)} 千億`, 'M1B餘額']
                  : [`${v}%`, 'M1B年增率']
                }
                labelFormatter={l => `月份：${l}`}
                contentStyle={{ background: '#0d1820', border: '1px solid #2a4060', fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="m1b" name="M1B餘額" fill="#4C9BB8" fillOpacity={0.5} />
              <Line yAxisId="right" type="monotone" dataKey="yoy_pct" name="M1B年增率"
                stroke="#f5c518" strokeWidth={2} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
          {m1b.length > 0 && (() => {
            const latest = m1b[m1b.length - 1];
            return (
              <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 13 }}>
                <div style={{ color: '#5a8090' }}>最新月份 <span style={{ color: '#7ab0cc' }}>{latest.ym}</span></div>
                <div style={{ color: '#5a8090' }}>年增率 <span style={{ ...S.num(latest.yoy_pct), fontSize: 16 }}>
                  {latest.yoy_pct != null ? (latest.yoy_pct > 0 ? '+' : '') + latest.yoy_pct.toFixed(2) + '%' : '-'}
                </span></div>
                <div style={{ color: '#5a8090' }}>M1B 餘額 <span style={{ color: '#c0d8ea' }}>
                  {latest.m1b ? (latest.m1b / 1000).toFixed(0) + ' 億' : '-'}
                </span></div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── 大盤事件版面：注意股/處置股/庫藏股/內部人/重訊/訴訟 ── */}
      {Object.values(events).some(arr => arr.length > 0) && (() => {
        const TAB_META = [
          { key: 'announcement', icon: '📢', label: '重訊公告' },
          { key: 'attention',    icon: '👁️', label: '注意股' },
          { key: 'disposal',     icon: '🚧', label: '處置股' },
          { key: 'insider',      icon: '👤', label: '內部人持股' },
          { key: 'treasury',     icon: '🏦', label: '庫藏股' },
          { key: 'lawsuit',      icon: '⚖️', label: '訴訟案件' },
        ];
        const ANN_CATS = ['全部','人事異動','重大合約','合併收購','庫藏股','減資增資','財務危機','自結損益'];

        const currentList = (() => {
          const list = events[eventsTab] || [];
          if (eventsTab === 'announcement' && annCat !== '全部') {
            return list.filter(e => e.category === annCat);
          }
          return list;
        })();

        return (
          <div style={_sec}>
            <div style={S.title}>📋 大盤事件（最近 7 天）</div>

            {/* Tab 列 */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {TAB_META.map(t => (
                <div key={t.key}
                  onClick={() => setEventsTab(t.key)}
                  style={{
                    padding: '5px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 12,
                    background: eventsTab === t.key ? '#1a3a5a' : '#0d1520',
                    border: `1px solid ${eventsTab === t.key ? '#4C9BB8' : '#2a3a4a'}`,
                    color: eventsTab === t.key ? '#4C9BB8' : '#7ab0cc',
                    fontWeight: eventsTab === t.key ? 700 : 400,
                  }}>
                  {t.icon} {t.label}
                  <span style={{ marginLeft: 4, color: '#5a8090', fontSize: 10 }}>
                    ({(events[t.key] || []).length})
                  </span>
                </div>
              ))}
            </div>

            {/* 重訊分類篩選 */}
            {eventsTab === 'announcement' && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                {ANN_CATS.map(c => (
                  <div key={c} onClick={() => setAnnCat(c)}
                    style={{
                      padding: '3px 10px', borderRadius: 12, cursor: 'pointer', fontSize: 11,
                      background: annCat === c ? '#f5c51830' : 'transparent',
                      border: `1px solid ${annCat === c ? '#f5c518' : '#2a3a4a'}`,
                      color: annCat === c ? '#f5c518' : '#5a8090',
                    }}>
                    {c}
                  </div>
                ))}
              </div>
            )}

            {/* 事件清單 */}
            {currentList.length === 0 ? (
              <div style={{ color: '#4a6878', fontSize: 13, padding: '16px 0' }}>
                最近 7 天無相關資料
              </div>
            ) : (
              <div style={{ maxHeight: 560, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#0a0f1a' }}>
                    <tr style={{ borderBottom: '1px solid #2a3a4a' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', color: '#7ab0cc', width: 80, fontSize: 14 }}>日期</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', color: '#7ab0cc', width: 100, fontSize: 14 }}>股票</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', color: '#7ab0cc', fontSize: 14 }}>內容</th>
                      {eventsTab === 'announcement' && (
                        <th style={{ padding: '8px 10px', textAlign: 'left', color: '#7ab0cc', width: 80, fontSize: 14 }}>分類</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {currentList.slice(0, 100).map((e, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #131c26' }}>
                        <td style={{ padding: '7px 10px', color: '#7a9ab8', fontSize: 13 }}>
                          {e.date?.slice(5)}
                        </td>
                        <td style={{ padding: '7px 10px' }}>
                          <span style={{ color: '#4C9BB8', fontSize: 13, fontWeight: 600 }}>{e.stock_id}</span>
                          <span style={{ color: '#aac', fontSize: 12, marginLeft: 5 }}>{e.name}</span>
                        </td>
                        <td style={{ padding: '7px 10px', color: '#d8e4ee', fontSize: 14, lineHeight: 1.5 }}>
                          {e.title}
                        </td>
                        {eventsTab === 'announcement' && (
                          <td style={{ padding: '7px 10px' }}>
                            <span style={{
                              padding: '3px 8px', borderRadius: 8, fontSize: 12,
                              background: '#1a2a3a', color: '#7ab0cc',
                            }}>{e.category}</span>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {currentList.length > 100 && (
                  <div style={{ textAlign: 'center', color: '#7a9ab8', fontSize: 12, padding: 8 }}>
                    顯示前 100 則,共 {currentList.length} 則
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}


    </div>
  );
}
